import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { formatTimestamp, getColorForDarkMode } from 'utils'
import { format } from 'date-fns'
import SendIcon from '@mui/icons-material/Send'
import { authenticatedUserAtom, toastAtom } from 'state'
import { SetterOrUpdater, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  useFetchAnonymousRecentConversations,
  useFetchConversationsGroupedByTwin,
  useFetchRecentConversations,
} from 'hooks/use-conversation'
import {
  Twin,
  TwinConversation,
  TwinConversationGroup,
  TwinMessage,
  TwinMessageFeedbackStatus,
  TwinContextSource,
  AnonymousUser,
  OnboardingStep,
  Reference,
  TwinMessageAnswer,
  TwinConversationType,
  TwinConversationTag,
} from 'api/model'
import ProfilePhoto from 'components/ProfilePhoto/ProfilePhoto'
import SearchIcon from '@mui/icons-material/Search'
import { conversationsGroupedByTwinAtom, recentConversationsAtom } from 'state/conversations'
import AddConversationModal from '../JelledBots/AddConversationPopover'
import { useFetchConversationMessages } from 'hooks/use-messages'
import { twinsAtom } from 'state/twins'
import { useFetchAnonymousUser } from 'hooks/use-twin'
import { useActiveOrganization } from 'hooks/use-organization'
import {
  twinsAnswer,
  twinsAsk,
  twinsConversationViewed,
  twinsDeleteConversation,
  twinsFeedback,
  twinsGetMessageDetails,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import React from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt'
import { myTwinMessagesAtom } from 'state/messages'
import { LoadingButton } from '@mui/lab'
import { anonymousTwinAtom } from 'state/anonymous-twin'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import DebugSettings, { validateDebugSettings } from 'components/Debug/DebugSettings'
import { RelevanceConfig, getDebugProperties, useFetchDebugSettings } from 'hooks/use-debug'
import ConversationsFtuxModal from 'components/FtuxModal/ConversationsFtuxModal'
import withOnboardingStep from 'components/FTUE/withOnboardingStep'
import EmptyStateSection from 'components/MyTwin/EmptyStateSection'
import CallMadeOutlinedIcon from '@mui/icons-material/CallMadeOutlined'
import { useFetchSuggestedQuestions } from 'hooks/use-instructions'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import InfoIcon from '@mui/icons-material/Info'
import RefreshIcon from '@mui/icons-material/Refresh'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import AddOrEditNoteModal from 'components/MyTwin/AddOrEditNoteModal'
import ReferencesList from 'components/References/ReferencesList'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import { API, COLORS } from 'app-constants'
import { getToken } from 'hooks/use-auth-token'
import useFlag from 'hooks/use-flag'
import { AxiosError } from 'axios'
import { useInterval } from 'usehooks-ts'
import { TwinChatBubbleStack } from 'components/Chatbot'
import { groupConversationsByDay } from 'components/JelledBots'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

const RecentConversationCard = ({
  groupedConversationByDay,
  conversationId,
  setConversationId,
  isDeleteDisabled,
  onDeleteConversationClick,
}: {
  groupedConversationByDay: TwinConversationGroup
  conversationId: string
  setConversationId: (conversationId: string) => void
  isDeleteDisabled?: boolean
  onDeleteConversationClick?: (event: React.SyntheticEvent, conversationId: string) => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const isMobile = useCheckMobileScreen()

  const navigate = useNavigate()
  const setRecentConversations = useSetRecoilState(recentConversationsAtom)
  const setConversationsGroupedByTwin = useSetRecoilState<TwinConversationGroup[]>(conversationsGroupedByTwinAtom)

  const [isHovered, setIsHovered] = useState<boolean>(false)

  const getHasUnreadEdits = () => {
    // case 1: if date_viewed is empty, then there are unread edits
    if (!groupedConversationByDay.twin_conversations[0].date_viewed) {
      return true
    }

    // case 2: if date_viewed is before date_updated, then there are unread edits
    const dateViewed = new Date(groupedConversationByDay.twin_conversations[0].date_viewed ?? '')
    const dateUpdated = new Date(groupedConversationByDay.twin_conversations[0].date_updated ?? '')
    if (dateViewed < dateUpdated) {
      return true
    }

    return false
  }

  // should call conversation viewed endpoint
  const updateConversationViewedAt = async () => {
    if (!activeOrganization) {
      return
    }

    try {
      const conversation = groupedConversationByDay.twin_conversations[0]

      const response = await twinsConversationViewed(activeOrganization, conversation.id, {
        twin_id: conversation?.twin_id ?? '',
      })

      // should update the conversation in recoil in recent conversations
      setRecentConversations((prev) => {
        const updatedRecentConversations = prev.map((rc) => {
          if (rc.twin_conversations.find((c) => c.id === conversation.id)) {
            return {
              ...rc,
              twin_conversations: rc.twin_conversations.map((c) => {
                if (c.id === conversation.id) {
                  return response.data
                }
                return c
              }),
            }
          }
          return rc
        })
        return updatedRecentConversations
      })

      // should update the conversation in recoil in conversations grouped by twin
      setConversationsGroupedByTwin((prev) => {
        if (!prev) {
          return []
        }

        const updatedConversationsGroupedByTwin = prev.map((c) => {
          return {
            ...c,
            twin_conversations: c.twin_conversations.map((tc) => {
              if (tc.id === conversation.id) {
                return response.data
              }
              return tc
            }),
          }
        })

        return updatedConversationsGroupedByTwin
      })
    } catch {
      setToast({
        title: 'Failed to update conversation viewed at',
        severity: 'error',
      })
    }
  }

  return (
    <Stack
      key={groupedConversationByDay.twin_conversations[0].id}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      borderRadius="8px"
      onClick={() => {
        setConversationId(groupedConversationByDay.twin_conversations[0].id)
        navigate(
          `/jelledbots?twinId=${groupedConversationByDay.twin.id}&conversationId=${groupedConversationByDay.twin_conversations[0].id}`
        )

        // whenever we click on a recent conversation card, we should update the conversation viewed at property
        updateConversationViewedAt()
      }}
      sx={{
        cursor: 'pointer',
        py: 1,
        px: 2,
        backgroundColor:
          conversationId === groupedConversationByDay.twin_conversations[0].id ? COLORS.GRAY_8 : 'transparent',
        '&:hover': {
          backgroundColor: COLORS.GRAY_8,
        },
      }}>
      <Stack direction="row" alignItems="center" key={groupedConversationByDay.twin_conversations[0].id}>
        <ProfilePhoto
          src={groupedConversationByDay.twin.picture}
          size="36px"
          name={groupedConversationByDay.twin.name || groupedConversationByDay.twin.user.email}
        />

        <Stack ml={1} width="100%">
          <Typography
            variant="pSM"
            sx={{
              color: COLORS.GRAY_0,
            }}>
            {groupedConversationByDay.twin.name}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="body0"
              my="4px"
              sx={{
                flex: '0 0 130px',
                color: COLORS.GRAY_2,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
              {groupedConversationByDay.twin_conversations[0].title}
            </Typography>

            <Typography
              mb="4px"
              variant="body0"
              sx={{
                flex: '0 0 63px',
                color: COLORS.GRAY_2,
              }}>
              {format(
                new Date(
                  groupedConversationByDay.twin_conversations[0].date_updated ||
                    groupedConversationByDay.twin_conversations[0].date_created
                ),
                'h:mm a'
              )}
            </Typography>
          </Stack>
        </Stack>
        {getHasUnreadEdits() && !isHovered && (
          <IconButton
            sx={{
              p: '4px',
              width: '32px',
            }}>
            <Stack
              sx={{
                height: '10px',
                width: '10px',
                backgroundColor: COLORS.JELLED_PRIMARY_BLUE,
                borderRadius: '50%',
              }}
            />
          </IconButton>
        )}
        {!isDeleteDisabled && (isMobile || (!isMobile && isHovered)) && (
          <IconButton
            sx={{
              p: '4px',
            }}
            onClick={(e) => onDeleteConversationClick?.(e, groupedConversationByDay.twin_conversations[0].id)}>
            <DeleteOutlinedIcon sx={{ fill: COLORS.GRAY_3 }} />
          </IconButton>
        )}
      </Stack>
    </Stack>
  )
}

export const RecentConversationItem = ({
  day,
  groupedConversationsByDay,
  conversationId,
  setConversationId,
  setRecentConversations,
}: {
  day: string
  groupedConversationsByDay: Record<string, TwinConversationGroup[]>
  conversationId: string
  setConversationId: (conversationId: string) => void
  setRecentConversations?: SetterOrUpdater<TwinConversationGroup[]>
}) => {
  const theme = useTheme()
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const formattedTimestamp = useMemo(() => {
    return formatTimestamp(day)
  }, [day])

  const [pendingDeleteConversationId, setPendingDeleteConversationId] = useState<string>('')

  const [isDeletingConversation, setIsDeletingConversation] = useState<boolean>(false)
  const onDeleteConversationConfirmed = async () => {
    setIsDeletingConversation(true)

    if (!activeOrganization) {
      setIsDeletingConversation(false)
      return null
    }

    try {
      await twinsDeleteConversation(activeOrganization, pendingDeleteConversationId, {
        twin_id: groupedConversationsByDay[day][0].twin.id,
      })
      setPendingDeleteConversationId('')
      // we should also remove that conversation from recoil
      setRecentConversations?.((prev) => {
        const updatedRecentConversations = prev.filter((rc) => {
          return rc.twin_conversations[0].id !== pendingDeleteConversationId
        })
        return updatedRecentConversations
      })
      setConversationId('')
      setToast({
        title: 'Conversation deleted',
        severity: 'success',
      })
    } catch (error) {
      setToast({
        title: 'Failed to delete conversation',
        severity: 'error',
      })
    } finally {
      setIsDeletingConversation(false)
    }
  }

  const onDeleteConversationClick = (event: React.SyntheticEvent, conversationId: string) => {
    setPendingDeleteConversationId(conversationId)
    event?.stopPropagation()
  }

  // we disable deletion of conversations in the anonymous session
  const isDeleteDisabled = !activeOrganization

  return (
    <Stack mb={2}>
      <Typography
        variant="h7.25"
        mb={1}
        sx={{
          color: COLORS.GRAY_2,
        }}>
        {formattedTimestamp}
      </Typography>

      <Stack spacing={1}>
        {groupedConversationsByDay[day].map((rc) => (
          <RecentConversationCard
            key={rc.twin_conversations[0].id}
            groupedConversationByDay={rc}
            conversationId={conversationId}
            setConversationId={setConversationId}
            isDeleteDisabled={isDeleteDisabled}
            onDeleteConversationClick={onDeleteConversationClick}
          />
        ))}
      </Stack>

      <Dialog open={!!pendingDeleteConversationId}>
        <DialogTitle>Are you sure you want to delete this conversation?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setPendingDeleteConversationId('')
            }}
            disabled={isDeletingConversation}
            sx={{
              color: getColorForDarkMode(theme),
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onDeleteConversationConfirmed}
            disabled={isDeletingConversation}>
            Yes, delete it
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

// renders the list of recent conversations in the sidebar
const AnonymousRecentConversations = ({
  conversationId,
  setConversationId,
  twinOrgId,
  twinId,
}: {
  conversationId: string
  setConversationId: (conversationId: string) => void
  twinOrgId: string
  twinId: string
}) => {
  const { recentConversations, isLoading } = useFetchAnonymousRecentConversations(twinOrgId, twinId)

  const groupedConversationsByDay = useMemo(() => {
    return groupConversationsByDay(recentConversations)
  }, [recentConversations])
  const days = useMemo(() => {
    return Object.keys(groupedConversationsByDay)
  }, [groupedConversationsByDay])

  if (isLoading || recentConversations.length === 0) {
    return null
  }

  return (
    <Stack>
      {days.map((day) => (
        <RecentConversationItem
          day={day}
          groupedConversationsByDay={groupedConversationsByDay}
          conversationId={conversationId}
          setConversationId={setConversationId}
        />
      ))}
    </Stack>
  )
}

// renders the list of recent conversations in the sidebar
const RecentConversations = ({
  conversationId,
  setConversationId,
}: {
  conversationId: string
  setConversationId: (conversationId: string) => void
}) => {
  const isMobile = useCheckMobileScreen()

  // we need to keep of updatedSince value so that we can refetch with this value
  // the reason we need this is to see if we have newly updated conversations where some messages are stale
  const [updatedSince, setUpdatedSince] = useState<string>('')

  const { recentConversations, isLoading, setRecentConversations, refetchRecentConversations } =
    useFetchRecentConversations({
      shouldFetchOnMount: true,
    })

  useEffect(() => {
    if (recentConversations.length !== 0) {
      setUpdatedSince(new Date().toISOString())
    }
  }, [recentConversations])

  // every minute, we should refetch conversations with the latest updatedSince value
  useInterval(() => {
    refetchRecentConversations(updatedSince)
    setUpdatedSince(new Date().toISOString())
  }, 1000 * 60)

  const groupedConversationsByDay = useMemo(() => {
    return groupConversationsByDay(recentConversations)
  }, [recentConversations])
  const days = useMemo(() => {
    return Object.keys(groupedConversationsByDay)
  }, [groupedConversationsByDay])

  if (isLoading) {
    return <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
  }

  if (recentConversations.length === 0 && isMobile) {
    return <EmptyStateSection headerText="No recent conversations" />
  }

  return (
    <Stack>
      {days.map((day) => (
        <RecentConversationItem
          key={day}
          day={day}
          groupedConversationsByDay={groupedConversationsByDay}
          conversationId={conversationId}
          setConversationId={setConversationId}
          setRecentConversations={setRecentConversations}
        />
      ))}
    </Stack>
  )
}

const TwinConversationCard = ({
  twinConversation,
  setConversationId,
  conversationId,
  onDeleteConversationClick,
}: {
  twinConversation: TwinConversation
  setConversationId: (conversationId: string) => void
  conversationId: string
  onDeleteConversationClick: (event: React.SyntheticEvent, conversationId: string) => void
}) => {
  const isMobile = useCheckMobileScreen()
  const theme = useTheme()
  const [isHovered, setIsHovered] = useState<boolean>(false)

  const isSelected = conversationId === twinConversation.id

  // there are two scenarios - light / dark mode as well as whether or not the card is selected
  const getTextColor = () => {
    // in light mode, the text will always be black
    if (theme.palette.mode === 'light') {
      return COLORS.GRAY_0
    } else {
      // in dark mode, when the card is selected / hovered, the text will be black
      const isSelected = conversationId === twinConversation.id
      if (isHovered || isSelected) {
        return COLORS.GRAY_0
      }
    }
    return COLORS.PURE_WHITE
  }

  const getHasUnreadEdits = () => {
    // case 1: if date_viewed is empty, then there are unread edits
    if (!twinConversation.date_viewed) {
      return true
    }

    // case 2: if date_viewed is before date_updated, then there are unread edits
    const dateViewed = new Date(twinConversation.date_viewed)
    const dateUpdated = new Date(twinConversation.date_updated ?? '')
    if (dateViewed < dateUpdated) {
      return true
    }

    return false
  }

  return (
    <Stack mb={2} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <Stack
        borderRadius="8px"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={() => {
          setConversationId(twinConversation.id)
        }}
        sx={{
          cursor: 'pointer',
          py: 1,
          px: 2,
          backgroundColor: isSelected ? COLORS.GRAY_8 : 'transparent',
          '&:hover': {
            backgroundColor: COLORS.GRAY_8,
          },
        }}>
        <Stack>
          <Typography
            variant="pS"
            sx={{
              width: isMobile ? '72vw' : '179px',
              color: getTextColor(),
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
            {twinConversation.title}
          </Typography>
        </Stack>

        {!isHovered && getHasUnreadEdits() && (
          <Stack
            sx={{
              ml: '16px',
              height: '10px',
              width: '10px',
              backgroundColor: COLORS.JELLED_PRIMARY_BLUE,
              borderRadius: '50%',
              flex: '0 0 10px',
            }}
          />
        )}

        <IconButton
          disabled={!isHovered}
          onClick={(e) => onDeleteConversationClick(e, twinConversation.id)}
          sx={{
            opacity: isHovered ? 1 : 0,
          }}>
          <DeleteOutlinedIcon
            sx={{
              fill: getColorForDarkMode(theme, {
                isSelected: isSelected || isHovered,
              }),
            }}
          />
        </IconButton>
      </Stack>
    </Stack>
  )
}

// renders the list of conversations grouped by twin in the sidebar
const TwinConversations = ({
  conversationId,
  setConversationId,
}: {
  conversationId: string
  setConversationId: (conversationId: string) => void
}) => {
  const theme = useTheme()
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const [updatedSince, setUpdatedSince] = useState<string>('')

  const { conversationsGroupedByTwin, isLoading, setConversationsGroupedByTwin, refetchConversationsGroupedByTwin } =
    useFetchConversationsGroupedByTwin({
      groupByTwin: true,
      shouldFetchOnMount: true,
    })

  useEffect(() => {
    if (conversationsGroupedByTwin.length !== 0) {
      setUpdatedSince(new Date().toISOString())
    }
  }, [conversationsGroupedByTwin])

  // every minute, we should refetch conversations with the latest updatedSince value
  useInterval(() => {
    refetchConversationsGroupedByTwin(updatedSince)
    setUpdatedSince(new Date().toISOString())
  }, 1000 * 60)

  const [searchText, setSearchText] = useState<string>('')
  const onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value)
  }

  const [pendingDeleteConversationId, setPendingDeleteConversationId] = useState<string>('')

  const [isDeletingConversation, setIsDeletingConversation] = useState<boolean>(false)
  const onDeleteConversationConfirmed = async () => {
    setIsDeletingConversation(true)

    if (!activeOrganization) {
      setIsDeletingConversation(false)
      return null
    }

    try {
      await twinsDeleteConversation(activeOrganization, pendingDeleteConversationId, {
        twin_id: conversationsGroupedByTwin[0].twin.id,
      })
      // we should also remove that conversation from recoil
      setConversationsGroupedByTwin?.((prev) => {
        const updatedConversationsGroupedByTwin = prev.map((c) => {
          const updatedTwinConversations = c.twin_conversations.filter((tc) => {
            return tc.id !== pendingDeleteConversationId
          })

          return {
            ...c,
            twin_conversations: updatedTwinConversations,
          }
        })

        // filter out the conversations grouped by twin that don't have any conversations
        const filteredConversationsGroupedByTwin = updatedConversationsGroupedByTwin.filter((c) => {
          return c.twin_conversations.length !== 0
        })

        return filteredConversationsGroupedByTwin
      })

      setPendingDeleteConversationId('')
      setConversationId('')
      setToast({
        title: 'Conversation deleted',
        severity: 'success',
      })
    } catch (error) {
      setToast({
        title: 'Failed to delete conversation',
        severity: 'error',
      })
    } finally {
      setIsDeletingConversation(false)
    }
  }

  const onDeleteConversationClick = (event: React.SyntheticEvent, conversationId: string) => {
    setPendingDeleteConversationId(conversationId)
    event?.stopPropagation()
  }

  // TODO: add loading state
  if (isLoading) {
    return null
  }

  return (
    <Stack>
      <TextField
        placeholder="Search for twin"
        value={searchText}
        onChange={onSearchTextChange}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon
                sx={{
                  color: COLORS.GRAY_3,
                  marginRight: '-10px',
                }}
              />
            </InputAdornment>
          ),
        }}
        sx={{
          marginBottom: '16px',
          input: {
            color: 'secondary.main',
          },
          '& ::placeholder': {
            color: 'secondary.main',
          },
        }}
      />

      {conversationsGroupedByTwin.map((c) => (
        <Stack key={c.twin.id}>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <ProfilePhoto src={c.twin.picture} size="32px" name={c.twin.name || c.twin.user.email} />
            <Typography
              variant="h6"
              mb={1}
              sx={{
                color: getColorForDarkMode(theme),
              }}>
              {c.twin.name || c.twin.user.email}
            </Typography>
          </Stack>

          {c.twin_conversations.map((tc) => (
            <TwinConversationCard
              key={tc.id}
              twinConversation={tc}
              setConversationId={setConversationId}
              conversationId={conversationId}
              onDeleteConversationClick={onDeleteConversationClick}
            />
          ))}
        </Stack>
      ))}
      <Dialog open={!!pendingDeleteConversationId}>
        <DialogTitle>Are you sure you want to delete this conversation?</DialogTitle>
        <DialogActions>
          <Button
            onClick={() => {
              setPendingDeleteConversationId('')
            }}
            disabled={isDeletingConversation}
            sx={{
              color: getColorForDarkMode(theme),
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={onDeleteConversationConfirmed}
            disabled={isDeletingConversation}>
            Yes, delete it
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

enum ViewType {
  RECENT_CONVERSATIONS = 'Recent conversations',
  GROUPED_BY_TWIN = 'Grouped by twin',
}

export const ConversationsSidebar = ({
  conversationId,
  setConversationId,
  onAddConversationClick,
  twinOrgId,
  twinId,
}: {
  conversationId: string
  setConversationId: (conversationId: string) => void
  onAddConversationClick: () => void
  twinOrgId?: string
  twinId?: string
}) => {
  const isMobile = useCheckMobileScreen()

  const [searchParams] = useSearchParams()
  const twinIdFromUrl = searchParams.get('twinId')
  const twin = useRecoilValue(twinsAtom).find((t) => t.id === twinIdFromUrl)

  const [viewType] = useState<ViewType>(ViewType.RECENT_CONVERSATIONS)

  const renderContent = () => {
    if (twinOrgId) {
      return (
        <AnonymousRecentConversations
          conversationId={conversationId}
          setConversationId={setConversationId}
          twinOrgId={twinOrgId}
          twinId={twinId ?? ''}
        />
      )
    }

    switch (viewType) {
      case ViewType.RECENT_CONVERSATIONS:
        return <RecentConversations conversationId={conversationId} setConversationId={setConversationId} />
      case ViewType.GROUPED_BY_TWIN:
        return <TwinConversations conversationId={conversationId} setConversationId={setConversationId} />
      default:
        return null
    }
  }

  // if there is no conversation selected and if there is a twin id in the url, then we should show the new chat sidebar item
  const shouldShowNewChat = !conversationId && twinIdFromUrl

  return (
    <Stack
      sx={{
        overflowY: 'scroll',
        width: isMobile ? '100%' : '260px',
      }}>
      <Button
        variant="contained"
        color="primary"
        onClick={onAddConversationClick}
        startIcon={<AddCircleOutlineIcon />}
        sx={{
          width: 'fit-content',
          marginBottom: '32px',
          paddingTop: '0.5rem',
          paddingBottom: '0.5rem',
        }}>
        New conversation
      </Button>

      {shouldShowNewChat && twin && (
        <Stack
          p="12px 15px"
          mb={4}
          borderRadius="8px"
          sx={{
            backgroundColor: COLORS.GRAY_8,
          }}>
          <Typography variant="h6">New chat with {twin.name}</Typography>
        </Stack>
      )}
      {renderContent()}
    </Stack>
  )
}

const IntroTwinChatBubble = ({
  isReviewMode,
  twin,
  anonymousTwin,
}: {
  isReviewMode?: boolean
  twin?: Twin
  anonymousTwin: Twin | null
}) => {
  const isMobile = useCheckMobileScreen()

  return (
    <Stack
      borderRadius="20px"
      p="12px"
      position="relative"
      sx={{
        ml: '32px',
        mb: 2,
        width: isMobile ? 'auto' : 'fit-content',
        alignSelf: isReviewMode ? 'flex-end' : 'flex-start',
        backgroundColor: getBubbleBackgroundColor(false, undefined, isReviewMode),
      }}>
      {!isReviewMode && <TwinPhoto twin={twin} anonymousTwin={anonymousTwin} />}

      <Typography variant="body0">Hi! How can I help you today?</Typography>
    </Stack>
  )
}

export const TwinPhoto = ({ twin, anonymousTwin }: { twin?: Twin; anonymousTwin: Twin | null }) => {
  if (!twin && !anonymousTwin) {
    return null
  }

  let src
  let name
  if (twin) {
    src = twin.picture || twin.user.picture
    name = twin.name || `${twin.user.name}'s twin`
  } else if (anonymousTwin) {
    src = anonymousTwin.picture || anonymousTwin.user.picture
    name = anonymousTwin.name || `${anonymousTwin.user.name}'s twin`
  }

  return (
    <Stack
      position="absolute"
      sx={{
        left: '-32px',
        top: '50%',
        transform: 'translateY(-50%)',
      }}>
      <ProfilePhoto src={src} size="24px" name={name} />
    </Stack>
  )
}

const TwinMessageActions = ({
  isReviewMode,
  onSubmitFeedback,
  twinMessage,
}: {
  isReviewMode?: boolean
  onSubmitFeedback: (feedback: TwinMessageFeedbackStatus) => void
  twinMessage: TwinMessage
}) => {
  const setToast = useSetRecoilState(toastAtom)

  const thumbUpIcon =
    twinMessage.feedback === TwinMessageFeedbackStatus.positive ? (
      <ThumbUpAltIcon
        sx={{
          fill: COLORS.GREEN,
          height: '16px',
          width: '16px',
        }}
      />
    ) : (
      <ThumbUpOffAltIcon
        sx={{
          fill: COLORS.GRAY_3,
          height: '16px',
          width: '16px',
        }}
      />
    )

  const thumbDownIcon =
    twinMessage.feedback === TwinMessageFeedbackStatus.negative ? (
      <ThumbDownAltIcon
        sx={{
          fill: COLORS.RED,
          height: '16px',
          width: '16px',
        }}
      />
    ) : (
      <ThumbDownOffAltIcon
        sx={{
          fill: COLORS.GRAY_3,
          height: '16px',
          width: '16px',
        }}
      />
    )

  const onCopyClick = () => {
    if (twinMessage.twin_answer) {
      navigator.clipboard.writeText(twinMessage.twin_answer)
      setToast({ title: 'Copied the twin answer to clipboard', severity: 'success' })
    }
  }

  const onThumbsUpClick = () => {
    onSubmitFeedback(TwinMessageFeedbackStatus.positive)
  }

  const onThumbsDownClick = () => {
    onSubmitFeedback(TwinMessageFeedbackStatus.negative)
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing="5px"
      pl={4}
      mt={1}
      alignSelf={isReviewMode ? 'flex-end' : 'flex-start'}>
      <IconButton
        onClick={onCopyClick}
        sx={{
          height: '24px',
          width: '24px',
        }}>
        <ContentCopyIcon
          sx={{
            height: '16px',
            width: '16px',
            fill: COLORS.GRAY_3,
          }}
        />
      </IconButton>
      <IconButton
        sx={{
          height: '24px',
          width: '24px',
        }}
        onClick={onThumbsUpClick}>
        {thumbUpIcon}
      </IconButton>
      <IconButton
        sx={{
          height: '24px',
          width: '24px',
        }}
        onClick={onThumbsDownClick}>
        {thumbDownIcon}
      </IconButton>
    </Stack>
  )
}

function getBubbleBackgroundColor(isUserMessage: boolean, twinMessage?: TwinMessage, isReviewMode?: boolean) {
  if (isReviewMode) {
    // user messages should be blue
    if (isUserMessage) {
      return COLORS.GRAY_8
    }
    // bubbles with negative feedback should be red
    if (twinMessage?.feedback === TwinMessageFeedbackStatus.negative) {
      return COLORS.RED_10_PERCENT
    }
    // default to gray if not a user message and also not negative feedback
    return COLORS.JELLED_MID_BLUE_10_PERCENT
  } else {
    // user messages are gray and twin messages are blue
    return isUserMessage ? COLORS.JELLED_MID_BLUE_10_PERCENT : COLORS.GRAY_8
  }
}

// TODO: move this to utils
// this function takes in a string and returns an array of words and Link objects for words that are URLs
export function getTextWithLinks(answer: string) {
  if (!answer) {
    return ''
  }

  // split words by empty string
  const words = answer.split(' ')

  const wordsWithLinks = words.map((word, idx) => {
    // if word starts with http or www, then we should turn it into a link
    if (word.startsWith('www')) {
      return (
        <Link
          key={idx}
          href={`https://${word}`}
          target="_blank"
          sx={{
            // on hover, show underline
            '&:hover': {
              textDecoration: 'underline',
            },
          }}>
          {word}{' '}
        </Link>
      )
    } else if (word.startsWith('http')) {
      return (
        <Link
          key={idx}
          href={word}
          target="_blank"
          sx={{
            '&:hover': {
              textDecoration: 'underline',
            },
          }}>
          {word}{' '}
        </Link>
      )
    }
    // add a space after the word if the word is not the last word
    if (idx !== words.length - 1) {
      return word + ' '
    }
    return word
  })
  return wordsWithLinks
}

// this component shows a navigable list of references attached to a twin message
const MessageReferences = ({ references }: { references: Reference[] }) => {
  const [referenceIndex, setReferenceIndex] = useState<number>(0)

  const onNextClick = () => {
    if (referenceIndex === references.length - 1) {
      return
    }
    setReferenceIndex((prev) => prev + 1)
  }

  const onPreviousClick = () => {
    if (referenceIndex === 0) {
      return
    }
    setReferenceIndex((prev) => prev - 1)
  }

  if (references.length === 0) {
    return null
  }

  const currentReference = references[referenceIndex]
  const title = references.length > 1 ? `References ${referenceIndex + 1}/${references.length}` : 'Reference'

  return (
    <Stack
      p="16px 20px"
      sx={{
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        backgroundColor: COLORS.GRAY_8,
      }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb="4px">
        <Typography
          variant="h7.25"
          sx={{
            color: COLORS.GRAY_2,
          }}>
          {title}
        </Typography>

        <Stack direction="row" alignItems="center" spacing="4px">
          <IconButton disabled={referenceIndex === 0} onClick={onPreviousClick} size="small">
            <KeyboardArrowLeftIcon
              sx={{
                fill: COLORS.GRAY_3,
              }}
            />
          </IconButton>

          <IconButton disabled={referenceIndex === references.length - 1} onClick={onNextClick} size="small">
            <KeyboardArrowRightIcon
              sx={{
                fill: COLORS.GRAY_3,
              }}
            />
          </IconButton>
        </Stack>
      </Stack>

      <Tooltip title={currentReference.url} enterTouchDelay={0}>
        <Link
          href={`${currentReference.url}`}
          target="_blank"
          sx={{
            color: COLORS.GRAY_0,
            width: '100%',
            maxWidth: '300px',
            textDecoration: 'underline',
          }}>
          {currentReference.title}
        </Link>
      </Tooltip>
    </Stack>
  )
}

export const TwinChatBubble = ({
  isReviewMode,
  twinMessage,
  hydrateConversationMessage,
  twinOrgId,
  hydrateConversationReviewRequests,
  editedTwinMessageId,
  setEditedTwinMessageId,
}: {
  isReviewMode?: boolean
  twinMessage: TwinMessage
  hydrateConversationMessage?: (twinMessage: TwinMessage) => void
  twinOrgId?: string
  hydrateConversationReviewRequests?: (reviewRequests: number) => void
  editedTwinMessageId?: string
  setEditedTwinMessageId?: React.Dispatch<React.SetStateAction<string>>
}) => {
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const setToast = useSetRecoilState(toastAtom)

  const setMyTwinMessages = useSetRecoilState(myTwinMessagesAtom)
  const setRecentConversations = useSetRecoilState(recentConversationsAtom)
  const setConversationsGroupedByTwin = useSetRecoilState<TwinConversationGroup[]>(conversationsGroupedByTwinAtom)

  const twin = useRecoilValue(twinsAtom).find((t) => t.id === twinMessage.twin_id)
  const anonymousTwin = useRecoilValue(anonymousTwinAtom)

  const [editedTwinMessage, setEditedTwinMessage] = useState<string>(
    twinMessage.user_answer || twinMessage.twin_answer || ''
  )
  const onEditedTwinMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSubmitCount(0)
    setEditedTwinMessage(event.target.value)
  }
  const [isSavingUserAnswer, setIsSavingUserAnswer] = useState<boolean>(false)
  // state for whether or not the answer should be scoped to the user
  const [scopeToUser, setScopeToUser] = useState<boolean>(true)
  const [submitCount, setSubmitCount] = useState<number>(0)

  // state for adding a note
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState<boolean>(false)

  // state for references
  const [references, setReferences] = useState<Reference[]>(twinMessage.references || [])

  if (!twin && !anonymousTwin) {
    return null
  }

  const hydrateRecentConversations = (conversationId: string) => {
    setRecentConversations((prev) => {
      const updatedRecentConversations = prev.map((rc) => {
        const updatedTwinConversations = rc.twin_conversations.map((tc) => {
          if (tc.id === conversationId && tc.uua_count > 0) {
            return {
              ...tc,
              date_viewed: new Date().toISOString(),
            }
          }
          return tc
        })

        return {
          ...rc,
          twin_conversations: updatedTwinConversations,
        }
      })
      return updatedRecentConversations
    })
  }

  const hydrateMyTwinMessages = (twinMessage: TwinMessage) => {
    setMyTwinMessages((prev) => {
      const updatedMyTwinMessages = prev.map((m) => {
        if (m.id === twinMessage.id) {
          return twinMessage
        }
        return m
      })
      return updatedMyTwinMessages
    })
  }

  const hydrateConversationsGroupedByTwin = (conversationId: string) => {
    setConversationsGroupedByTwin((prev) => {
      if (!prev) {
        return []
      }

      const updatedConversationsGroupedByTwin = prev.map((c) => {
        const updatedTwinConversations = c.twin_conversations.map((tc) => {
          if (tc.id === conversationId) {
            return {
              ...tc,
              date_viewed: new Date().toISOString(),
            }
          }
          return tc
        })

        return {
          ...c,
          twin_conversations: updatedTwinConversations,
        }
      })
      return updatedConversationsGroupedByTwin
    })
  }

  const onSubmitAnonymousFeedback = async (feedback: TwinMessageFeedbackStatus) => {
    if (!twinOrgId) {
      return
    }

    try {
      const response = await twinsFeedback(twinOrgId, {
        twin_id: twinMessage.twin_id,
        id: twinMessage.id,
        feedback,
      })

      const getToastTitle = () => {
        switch (feedback) {
          case TwinMessageFeedbackStatus.positive:
            return 'Thanks for sharing your feedback!'
          case TwinMessageFeedbackStatus.negative:
            return 'We will notify the twin owner to update their answer for this question'
        }
      }

      // used to update the escalations navigation menu item badge with the correct number of escalations
      hydrateConversationMessage?.(response.data)
      hydrateMyTwinMessages(response.data)

      // used to update the badge that shows how many user answers still needs feedback
      hydrateRecentConversations(twinMessage.conversation_id)
      hydrateConversationsGroupedByTwin(twinMessage.conversation_id)

      setToast({
        title: getToastTitle(),
        severity: 'success',
      })
    } catch (Error) {
      setToast({ title: 'Failed to submit feedback', severity: 'error' })
    }
  }

  const onSubmitFeedback = async (feedback: TwinMessageFeedbackStatus) => {
    if (!activeOrganization) {
      return
    }

    try {
      const response = await twinsFeedback(activeOrganization, {
        twin_id: twinMessage.twin_id,
        id: twinMessage.id,
        feedback,
      })

      const getToastTitle = () => {
        switch (feedback) {
          case TwinMessageFeedbackStatus.positive:
            return 'Thanks for sharing your feedback!'
          case TwinMessageFeedbackStatus.negative:
            return 'We will notify the twin owner to update their answer for this question'
        }
      }

      // used to update the escalations navigation menu item badge with the correct number of escalations
      hydrateConversationMessage?.(response.data)
      hydrateMyTwinMessages(response.data)

      // used to update the badge that shows how many user answers still needs feedback
      hydrateRecentConversations(twinMessage.conversation_id)
      hydrateConversationsGroupedByTwin(twinMessage.conversation_id)

      setToast({
        title: getToastTitle(),
        severity: 'success',
      })
    } catch (Error) {
      setToast({ title: 'Failed to submit feedback', severity: 'error' })
    }
  }

  const onSubmitUserAnswer = async () => {
    if (!activeOrganization || !editedTwinMessage) {
      return
    }

    try {
      setSubmitCount((prev) => prev + 1)
      setIsSavingUserAnswer(true)

      const twinMessageAnswer: TwinMessageAnswer = {
        answer: editedTwinMessage,
        id: twinMessage.id,
        references: references.length > 0 ? references : undefined,
        save_useranswer: true,
        twin_id: twinMessage.twin_id,
        scope_to_user: scopeToUser,
      }

      const response = await twinsAnswer(activeOrganization, twinMessageAnswer)

      // we need to take the response and then update the local state
      hydrateConversationMessage?.(response.data)
      hydrateMyTwinMessages(response.data)

      // we should only decrement if there was previously no user answer but now there is
      if (!twinMessage.user_answer && response.data.user_answer) {
        hydrateConversationReviewRequests?.(-1)
      }

      setToast({
        title: 'Your answer has been saved',
        severity: 'success',
      })

      // clears the edited twin message id
      setEditedTwinMessageId?.('')
    } catch (Error) {
      setToast({ title: 'Failed to save your answer', severity: 'error' })
    } finally {
      setIsSavingUserAnswer(false)
    }
  }

  const onCancelEditTwinMessageClick = () => {
    // clears the edited twin message id
    setEditedTwinMessageId?.('')
  }

  const onAddNoteClick = () => {
    setIsAddNoteModalOpen(true)
  }

  const onEditTwinMessageClick = () => {
    setEditedTwinMessage(twinMessage.user_answer || twinMessage.twin_answer || '')
    setEditedTwinMessageId?.(twinMessage.id)
  }

  const getEditedText = () => {
    if (isReviewMode) {
      return 'Edited'
    }
    return `Edited by ${twinMessage.twin_user_name} on ${format(
      new Date(twinMessage.date_updated),
      'MMM dd, yyyy'
    )} at ${format(new Date(twinMessage.date_updated), 'h:mm a')}`
  }

  const hasUserAnswer = !!twinMessage.user_answer

  // we only show the inline edit for the currently edited twin message
  if (twinMessage.id === editedTwinMessageId) {
    return (
      <Stack>
        <TextField
          multiline
          placeholder="Update your twin answer"
          value={editedTwinMessage}
          onChange={onEditedTwinMessageChange}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={onAddNoteClick}>
                  <AttachFileIcon
                    sx={{
                      height: '16px',
                      width: '16px',
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            border: '1px solid #2467F4',
            input: {
              color: 'secondary.main',
            },
            '& ::placeholder': {
              color: 'secondary.main',
            },
          }}
        />

        <Stack direction="row" alignItems="center" spacing={1} width="100%" my="8px">
          <Checkbox
            color="primary"
            checked={scopeToUser}
            onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
              setScopeToUser(checked)
            }}
            sx={{
              marginRight: '0',
              padding: 0,
            }}
          />
          <Typography variant="pS">Scope this answer to the user that is asking the question</Typography>
        </Stack>

        <Typography
          variant="pXS"
          sx={{
            color: COLORS.GRAY_3,
            mb: '8px',
          }}>
          Updating this message will notify the other user and your response will be saved in your twin's knowledge
          base.
        </Typography>

        <ReferencesList references={references} setReferences={setReferences} submitCount={submitCount} />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent={isReviewMode ? 'flex-end' : 'flex-start'}
          spacing={2}>
          <Button size="small" onClick={onCancelEditTwinMessageClick}>
            Cancel
          </Button>
          <LoadingButton loading={isSavingUserAnswer} variant="contained" size="small" onClick={onSubmitUserAnswer}>
            Save
          </LoadingButton>
        </Stack>
        {isAddNoteModalOpen && (
          <AddOrEditNoteModal
            noteId={''}
            onClose={() => setIsAddNoteModalOpen(false)}
            onAddNote={() => {}}
            onEditNote={() => {}}
            onHydrateFullNote={() => {}}
          />
        )}
      </Stack>
    )
  }

  return (
    <Stack
      sx={{
        alignSelf: isReviewMode ? 'flex-end' : 'flex-start',
      }}>
      {twinMessage.user_answer && (
        <Typography
          variant="body0"
          sx={{
            color: COLORS.GRAY_3,
            alignSelf: isReviewMode ? 'flex-end' : 'flex-start',
            mb: '4px',
            ml: isReviewMode ? '0' : '44px',
            mr: isReviewMode ? '10px' : '0',
          }}>
          {getEditedText()}
        </Typography>
      )}

      <Stack direction="row" alignItems="center" spacing={1}>
        {isReviewMode && twinMessage.feedback === TwinMessageFeedbackStatus.negative && (
          <ThumbDownAltIcon
            sx={{
              color: COLORS.GRAY_2,
              height: '16px',
              width: '16px',
            }}
          />
        )}

        {isReviewMode && !twinMessage.user_answer && !twinMessage.twin_answer ? (
          <Stack direction="row" spacing={1}>
            <InfoIcon
              sx={{
                color: COLORS.RED,
                height: '16px',
                width: '16px',
              }}
            />

            <Typography
              variant="pXS"
              sx={{
                color: COLORS.RED,
              }}>
              Failed to generate a twin answer
            </Typography>
          </Stack>
        ) : (
          <TwinChatBubbleStack
            sx={{
              width: isMobile ? 'auto' : 'fit-content',
              backgroundColor: getBubbleBackgroundColor(false, twinMessage, isReviewMode),
              boxShadow: hasUserAnswer && !isReviewMode ? '0px 0px 20px 0px rgba(127, 169, 255, 0.90)' : 'none',
            }}>
            {!isReviewMode && (
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <TwinPhoto twin={twin} anonymousTwin={anonymousTwin} />
              </Stack>
            )}

            {twinMessage.user_answer && ( // we should default to showing the user answer if it exists and fallback to the twin answer
              <Typography
                variant="body0"
                sx={{
                  p: '12px',
                  whiteSpace: 'pre-line',
                }}>
                {getTextWithLinks(twinMessage?.user_answer ?? '')}
              </Typography>
            )}

            {!twinMessage.user_answer && twinMessage.twin_answer && (
              <Typography
                variant="body0"
                sx={{
                  p: '12px',
                  whiteSpace: 'pre-line',
                }}>
                {getTextWithLinks(twinMessage?.twin_answer ?? '')}
              </Typography>
            )}
            <MessageReferences references={references} />
          </TwinChatBubbleStack>
        )}
      </Stack>
      {!isReviewMode && (
        <TwinMessageActions
          isReviewMode={isReviewMode}
          onSubmitFeedback={anonymousTwin ? onSubmitAnonymousFeedback : onSubmitFeedback}
          twinMessage={twinMessage}
        />
      )}
      {isReviewMode && (
        <Button
          disableRipple
          startIcon={
            <EditOutlinedIcon
              sx={{
                fill: COLORS.GRAY_3,
                height: '16px',
                width: '16px',
                mr: '-4px',
              }}
            />
          }
          onClick={onEditTwinMessageClick}
          sx={{
            backgroundColor: 'transparent',
            mt: '4px',
            width: 'fit-content',
            color: COLORS.GRAY_3,
            fontSize: '12px',
            fontWeight: 600,
            alignSelf: 'flex-end',
            '&:hover': {
              backgroundColor: 'transparent',
              color: COLORS.GRAY_0,
            },
            // on hover, make the EditOutlinedIcon fill turn black
            '&:hover .MuiSvgIcon-root': {
              fill: COLORS.GRAY_0,
            },
          }}>
          Edit
        </Button>
      )}
    </Stack>
  )
}

export const SuggestedChatBubble = ({
  onSuggestionClick,
  text,
}: {
  onSuggestionClick: (text: string) => void
  text: string
}) => {
  return (
    <Stack
      direction="row"
      borderRadius="40px"
      alignSelf="flex-end"
      alignItems="center"
      width="fit-content"
      p="8px 12px"
      mb={1}
      onClick={() => onSuggestionClick(text)}
      sx={{
        backgroundColor: COLORS.SPECIAL_FILL,
        cursor: 'pointer',
        border: `1px solid ${COLORS.SPECIAL_STROKE}`,
        alignSelf: 'flex-end',
        // make the animation slide in from the right
        animation: 'slideinright 0.5s',
        '@keyframes slideinright': {
          from: { transform: 'translateX(100%)' },
          to: { transform: 'translateX(0)' },
        },
      }}>
      <CallMadeOutlinedIcon
        sx={{
          height: '16px',
          width: '16px',
          color: COLORS.GRAY_0,
          marginRight: '4px',
        }}
      />
      <Typography
        variant="body0"
        textAlign="right"
        sx={{
          color: COLORS.GRAY_0,
        }}>
        {text}
      </Typography>
    </Stack>
  )
}

export const UserChatBubble = ({
  isReviewMode,
  anonymousUser,
  twinMessage,
  shouldRetry,
  onRetryClick,
}: {
  isReviewMode?: boolean
  anonymousUser: AnonymousUser | null
  twinMessage: TwinMessage
  shouldRetry: boolean // this is when the twin answer is null and the user wants to retry
  onRetryClick: (question: string) => void
}) => {
  const user = useRecoilValue(authenticatedUserAtom)

  if (!user && !anonymousUser) {
    return null
  }

  const isRetryEnabled = shouldRetry && !isReviewMode

  return (
    <Stack width="100%" mb={2}>
      {twinMessage.date_created && (
        <Typography variant="h7.25" textAlign={'center'} color="#666666" mb={3}>
          {format(new Date(twinMessage.date_created), 'MMM dd, yyyy')} at{' '}
          {format(new Date(twinMessage.date_created), 'hh:mm a')}
        </Typography>
      )}

      <Stack
        borderRadius="20px"
        alignSelf={isReviewMode ? 'flex-start' : 'flex-end'}
        width="fit-content"
        maxWidth="50%"
        p="12px"
        mb={1}
        sx={{
          backgroundColor: getBubbleBackgroundColor(true, twinMessage, isReviewMode),
        }}>
        <Typography variant="body0" textAlign="right">
          {twinMessage.question}
        </Typography>

        {isRetryEnabled && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing="4px"
            mt={1}
            alignSelf={isReviewMode ? 'flex-end' : 'flex-start'}>
            <InfoIcon
              sx={{
                color: COLORS.RED,
                height: '16px',
                width: '16px',
              }}
            />

            <Typography
              variant="pXS"
              sx={{
                color: COLORS.RED,
              }}>
              Failed
            </Typography>
          </Stack>
        )}
      </Stack>

      {isRetryEnabled && (
        <Button
          color="secondary"
          startIcon={<RefreshIcon color="secondary" />}
          onClick={() => {
            onRetryClick(twinMessage.question)
          }}
          sx={{
            width: 'fit-content',
            alignSelf: 'flex-end',
          }}>
          Retry
        </Button>
      )}
    </Stack>
  )
}

const MessagesScrollView = ({
  isReviewMode,
  conversationMessages,
  hydrateConversationMessage,
  twinOrgId,
  twin,
  onRetryClick,
  hydrateConversationReviewRequests,
  onSendQuestion,
  streamedMessage,
  pendingTwinMessage,
}: {
  isReviewMode?: boolean // review mode is when the twin owner is reviewing the twin's answers to another user's questions
  conversationMessages: TwinMessage[]
  hydrateConversationMessage: (twinMessage: TwinMessage) => void
  twinOrgId?: string
  twin?: Twin
  onRetryClick: (question: string) => void
  hydrateConversationReviewRequests?: (reviewRequests: number) => void
  onSendQuestion: (question: string) => void
  streamedMessage: string // the message that currently being streamed that represents the twin's response
  pendingTwinMessage: TwinMessage | null // the twin message that is currently being streamed
}) => {
  const isMobile = useCheckMobileScreen()

  const listRef = useRef<HTMLDivElement>(null)
  const firstMessageWithNegativeFeedbackRef = useRef<HTMLDivElement>(null)
  const firstMessageWithUserAnswerRef = useRef<HTMLDivElement>(null)

  // get the first twin message that has negative feedback
  const firstMessageWithNegativeFeedback = conversationMessages.find((cm) => {
    return cm.twin_answer && cm.feedback === TwinMessageFeedbackStatus.negative
  })

  const firstMessageWithUserAnswer = conversationMessages.find((cm) => {
    return cm.user_answer
  })

  // Function to scroll to the bottom of the list
  const scrollToBottom = () => {
    const list = listRef.current
    if (list) {
      list.scrollTop = list.scrollHeight
    }
  }

  // function to scroll to the first twin message that has negative feedback
  const scrollToFirstMessageWithNegativeFeedback = useCallback(() => {
    const firstMessageWithNegativeFeedback = firstMessageWithNegativeFeedbackRef.current
    if (firstMessageWithNegativeFeedback) {
      firstMessageWithNegativeFeedback.scrollIntoView({ behavior: 'smooth' })
    }
  }, [firstMessageWithNegativeFeedbackRef])

  // function to scroll to the first twin message that has a user answer
  const scrollToFirstMessageWithUserAnswer = useCallback(() => {
    const firstMessageWithUserAnswer = firstMessageWithUserAnswerRef.current
    if (firstMessageWithUserAnswer) {
      firstMessageWithUserAnswer.scrollIntoView({ behavior: 'smooth' })
    }
  }, [firstMessageWithUserAnswerRef])

  // as we are generating the streamed message, we should scroll to the bottom
  useEffect(() => {
    // debounce scroll by 100ms
    const timeout = setTimeout(() => {
      scrollToBottom()
    }, 10)

    return () => {
      clearTimeout(timeout)
    }
  }, [streamedMessage])

  useEffect(() => {
    // if we are in review mode, we should scroll to the first message that has negative feedback
    if (isReviewMode) {
      if (firstMessageWithNegativeFeedback) {
        scrollToFirstMessageWithNegativeFeedback()
      } else {
        scrollToBottom()
      }
    } else {
      // if there is a twin message that has a user answer, we should scroll to the first one
      if (firstMessageWithUserAnswer) {
        scrollToFirstMessageWithUserAnswer()
      } else {
        scrollToBottom()
      }
    }
  }, [
    firstMessageWithNegativeFeedback,
    firstMessageWithUserAnswer,
    isReviewMode,
    scrollToFirstMessageWithNegativeFeedback,
    scrollToFirstMessageWithUserAnswer,
  ])

  // state for which twin message is actively being edited
  const [editedTwinMessageId, setEditedTwinMessageId] = useState<string>('')

  // autoscroll user to the bottom whenever we are in editing mode for a user answer
  useEffect(() => {
    scrollToBottom()
  }, [editedTwinMessageId])

  useEffect(() => {
    // if we are not in review mode and if there is no twin messages with a user answer, we should scroll to the bottom
    if (!isReviewMode && !firstMessageWithUserAnswer) {
      scrollToBottom()
    }
  }, [conversationMessages, isReviewMode, firstMessageWithUserAnswer])

  const [getAnonymousUser] = useFetchAnonymousUser()
  const anonymousUser = getAnonymousUser()

  const { suggestedQuestions } = useFetchSuggestedQuestions(twin?.id ?? '')

  // we should automatically send the suggested question if the user clicks on it
  const onSuggestionClick = (text: string) => {
    onSendQuestion(text)
  }

  return (
    <Stack
      ref={listRef}
      width="auto"
      sx={{
        height: isMobile ? '53vh' : isReviewMode ? '70vh' : '100vh',
        overflowY: 'scroll',
        scrollBehavior: 'smooth',
        mb: 12,
      }}>
      <IntroTwinChatBubble isReviewMode={isReviewMode} twin={twin} anonymousTwin={twin || null} />

      {suggestedQuestions.map((sq, idx) => {
        return <SuggestedChatBubble key={idx} text={sq} onSuggestionClick={onSuggestionClick} />
      })}

      {conversationMessages.map((cm) => {
        let ref = null
        if (firstMessageWithUserAnswer) {
          ref = cm.id === firstMessageWithUserAnswer?.id ? firstMessageWithUserAnswerRef : null
        } else if (firstMessageWithNegativeFeedback) {
          ref = cm.id === firstMessageWithNegativeFeedback?.id ? firstMessageWithNegativeFeedbackRef : null
        }

        return (
          <Stack ref={ref} id={cm.id} key={cm.id} mb={2}>
            <UserChatBubble
              isReviewMode={isReviewMode}
              shouldRetry={!cm.twin_answer}
              anonymousUser={anonymousUser}
              twinMessage={cm}
              onRetryClick={onRetryClick}
            />
            <TwinChatBubble
              editedTwinMessageId={editedTwinMessageId}
              setEditedTwinMessageId={setEditedTwinMessageId}
              isReviewMode={isReviewMode}
              twinOrgId={twinOrgId}
              hydrateConversationMessage={hydrateConversationMessage}
              twinMessage={cm}
              hydrateConversationReviewRequests={hydrateConversationReviewRequests}
            />
          </Stack>
        )
      })}

      {pendingTwinMessage?.date_created && (
        <Typography variant="h7.25" textAlign={'center'} color="#666666" mb={3}>
          {format(new Date(pendingTwinMessage.date_created), 'MMM dd, yyyy')} at{' '}
          {format(new Date(pendingTwinMessage.date_created), 'hh:mm a')}
        </Typography>
      )}

      {pendingTwinMessage && ( // if there is a streamed message, we should also show the user's question
        <Stack
          borderRadius="20px"
          alignSelf="flex-end"
          width="fit-content"
          maxWidth="50%"
          p="12px"
          mb={2}
          sx={{
            backgroundColor: COLORS.GRAY_8,
          }}>
          <Typography variant="body0" textAlign="right">
            {pendingTwinMessage.question}
          </Typography>
        </Stack>
      )}

      {streamedMessage && (
        <Stack>
          <TwinChatBubbleStack
            sx={{
              width: isMobile ? 'auto' : 'fit-content',
              backgroundColor: COLORS.JELLED_MID_BLUE_10_PERCENT,
            }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <TwinPhoto twin={twin} anonymousTwin={twin || null} />
            </Stack>

            <Typography
              variant="body0"
              sx={{
                p: '12px',
                whiteSpace: 'pre-line',
              }}>
              {getTextWithLinks(streamedMessage)}
            </Typography>
          </TwinChatBubbleStack>
        </Stack>
      )}
    </Stack>
  )
}

type StreamedTwinsAskOptions = {
  relevanceConfig?: RelevanceConfig
  conversation?: TwinConversation
  retriedQuestion?: string
  setTempVerboseOutput?: React.Dispatch<React.SetStateAction<string>>
  setTempContextSources?: React.Dispatch<React.SetStateAction<TwinContextSource[]>>
  setConversationId?: React.Dispatch<React.SetStateAction<string>>
}

export async function streamedTwinsAsk(
  activeOrganization: string,
  twinIdToUse: string,
  question: string,
  verbose: boolean,
  setPendingTwinMessage: React.Dispatch<React.SetStateAction<TwinMessage | null>>, // this is used to show the timestamp / and question of the pending twin message
  setTempTwinMessage: React.Dispatch<React.SetStateAction<TwinMessage | null>>,
  setStreamedMessage: React.Dispatch<React.SetStateAction<string>>,
  setTempTwinConversation: React.Dispatch<React.SetStateAction<TwinConversation | null>>,
  isInternalConversation?: boolean,
  options: StreamedTwinsAskOptions = {},
  useMobius: boolean = false
) {
  // this will be used at the very end to make another API call to update the conversation with the final twin message
  let twinMessageId = ''

  let mobiusEndpoint = useMobius ? 'dialog-stream-mobius' : 'dialog-stream'
  const response = await fetch(
    // TODO - reset back to dialog-stream when the new API is ready
    `${API.ORIGIN}/api/v1/orgs/${activeOrganization}/twins/${twinIdToUse}/${mobiusEndpoint}?verbose=${verbose}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({
        twin_id: twinIdToUse,
        conversation_id: options.conversation?.id,
        question: options.retriedQuestion || question,
        conversation_tag: isInternalConversation ? TwinConversationTag.internal : undefined,
        relevance_config: options.relevanceConfig && verbose ? options.relevanceConfig : undefined,
        timezone_offset: -new Date().getTimezoneOffset(),
      }),
    }
  )

  if (!response.body) return
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader()

  // used to keep track of chunks that are not valid JSON
  let partialLine = ''

  while (true) {
    var { value, done } = await reader.read()

    if (done || !value) {
      break
    }
    if (value) {
      // trim value if there is whitespace at the end of value
      value = value.trim()

      // parse each line
      const lines = value.split('\n')
      // eslint-disable-next-line no-loop-func
      lines.forEach((l) => {
        if (l.trim() === '') {
          // skip this loop
          return
        }

        let responseForLine: any
        try {
          // it is possible that l is a partial JSON object in which case we want to aggregate
          responseForLine = JSON.parse(l)
        } catch (e) {
          partialLine += l
        }

        let responseForPartialLine: any
        try {
          // attempt to parse partialLine
          responseForPartialLine = JSON.parse(partialLine)
          // if successful, set partialLine to empty string
          partialLine = ''
        } catch (e) {
          // do nothing
        }

        // there's a special case where we got an error back from the server where we should just set the temp twin message and temp conversation
        if (response.statusText === 'Internal Server Error') {
          setTempTwinMessage(responseForLine.twin_message)

          if (!options.conversation?.id) {
            setTempTwinConversation(responseForLine.twin_conversation)
          }
          options.setConversationId?.(responseForLine.twin_conversation.id)
          setStreamedMessage('')

          if (responseForLine.verbose_output) {
            options.setTempVerboseOutput?.(responseForLine.verbose_output)
          }
          if (responseForLine.context_sources) {
            options.setTempContextSources?.(responseForLine.context_sources)
          }

          // return an error here so that we can stop the loop
          throw new Error('Internal Server Error')
        }

        // when the response starts, we should update the conversation with the first twin message
        if (responseForLine.twin_message) {
          setPendingTwinMessage(responseForLine.twin_message)
          twinMessageId = responseForLine.twin_message.id
        }

        // if we have a full formed partial line, we should update the streamed message using that
        if (responseForPartialLine?.streamed_message) {
          setStreamedMessage((prev) => prev + responseForPartialLine.streamed_message)
        } else if (responseForLine?.streamed_message) {
          // otherwise, if we have responseForLine, we should update streamed message from that
          setStreamedMessage((prev) => prev + responseForLine.streamed_message)
        }
      })
    }
  }

  // when the streaming is finished, we make another API call to update the rest of the conversation / message state
  if (twinMessageId) {
    const response = await twinsGetMessageDetails(activeOrganization, twinIdToUse, twinMessageId)
    if (response.data) {
      if (response.data.twin_message) {
        setTempTwinMessage(response.data.twin_message)
      }
      setPendingTwinMessage(null)
      setStreamedMessage('')

      if (response.data.twin_conversation) {
        if (!options.conversation?.id) {
          // we only need to set temp twin conversation if we are creating a new conversation
          setTempTwinConversation(response.data.twin_conversation)
        }
        options.setConversationId?.(response.data.twin_conversation.id)
      }

      // debugging purposes
      if (response.data.verbose_output) {
        options.setTempVerboseOutput?.(response.data.verbose_output)
      }
      if (response.data.context_sources) {
        options.setTempContextSources?.(response.data.context_sources)
      }
    }
  }
}

export const Messages = ({
  isReviewMode,
  isSubmitting,
  setIsSubmitting,
  conversation,
  setConversationId,
  isLoadingConversationMessages,
  hydrateConversationMessages,
  hydrateConversationMessage,
  conversationMessages,
  verboseOutput,
  setVerboseOutput,
  contextSources,
  setContextSources,
  emptyStateText,
  twinOrgId, // used in the context of an anonymous conversation
  hydrateConversationReviewRequests,
  setIsCreatingNewConversation,
  hydrateRecentConversations,
  hydrateConversationsGroupedByTwin,
}: {
  isReviewMode?: boolean // used when we're displaying messages in a Review context (e.g. when we're reviewing escalations)
  isSubmitting: boolean
  setIsSubmitting: (isSubmitting: boolean) => void
  conversation?: TwinConversation
  setConversationId?: React.Dispatch<React.SetStateAction<string>>
  isLoadingConversationMessages: boolean
  hydrateConversationMessages: (twinMessage: TwinMessage) => void
  hydrateConversationMessage: (twinMessage: TwinMessage) => void
  conversationMessages: TwinMessage[]
  verboseOutput: string
  setVerboseOutput: (verboseOutput: string) => void
  contextSources: TwinContextSource[]
  setContextSources: (contextSources: TwinContextSource[]) => void
  emptyStateText?: string
  twinOrgId?: string
  hydrateConversationReviewRequests?: (reviewRequests: number) => void
  setIsCreatingNewConversation?: React.Dispatch<React.SetStateAction<boolean>>
  hydrateRecentConversations?: (twin: Twin, twinConversation: TwinConversation) => void
  hydrateConversationsGroupedByTwin?: (twin: Twin, twinConversation: TwinConversation) => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const twinIdForPendingConversation = searchParams.get('twinId') ?? ''
  // if conversation exists, we should use the twin id from the conversation, otherwise, we should use the twin id from the url
  const twinIdToUse = conversation?.twin_id || twinIdForPendingConversation

  const user = useRecoilValue(authenticatedUserAtom)
  const twin = useRecoilValue(twinsAtom).find((t) => t.id === twinIdToUse ?? '')
  const anonymousTwin = useRecoilValue(anonymousTwinAtom)

  // this is the user that has started a conversation with a twin
  const askerUser = conversation?.user

  const setToast = useSetRecoilState(toastAtom)

  // used for debugging purposes
  const {
    isVerboseLogModalOpen,
    setIsVerboseLogModalOpen,
    contextType,
    setContextType,
    searchType,
    setSearchType,
    contextSize,
    setContextSize,
    maxContextSize,
    setMaxContextSize,
    relevance,
    setRelevance,
    temperature,
    setTemperature,
    maxTemperature,
    setMaxTemperature,
    modelType,
    setModelType,
    summaryModelType,
    setSummaryModelType,
    emailIntentModelType,
    setEmailIntentModelType,
    distance,
    setDistance,
    combineSearch,
    setCombineSearch,
    setMapReduceDepth,
    mapReduceDepth,
    conversationType,
    setConversationType,
    isStreamingToggled,
    setIsStreamingToggled,
    isMobiusToggled,
    setIsMobiusToggled,
  } = useFetchDebugSettings()

  useEffect(() => {
    if (conversation?.type) {
      setConversationType(conversation.type)
    }
  }, [conversation, setConversationType])

  const [question, setQuestion] = useState<string>('')
  const onQuestionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuestion(event.target.value)
  }

  // used when we are getting a stream response back for a question that was asked
  const [streamedMessage, setStreamedMessage] = useState<string>('')

  // we store the twin message in local state so that we can update the conversation messages once the while loop has finished
  // this is needed because if we try to update while in the while loop, hydrateConversationMessages is referencing a stale version of setConversationMessages
  const [tempTwinMessage, setTempTwinMessage] = useState<TwinMessage | null>(null)
  useEffect(() => {
    // we need to update conversation messages for the new conversation
    if (conversation && tempTwinMessage && !conversationMessages.find((c) => c.id === tempTwinMessage.id)) {
      hydrateConversationMessages(tempTwinMessage)
      setTempTwinMessage(null)
    }
  }, [tempTwinMessage, conversation, hydrateConversationMessages, conversationMessages])

  // this holds the twin message that is currently being streamed, it will be cleared when the streaming has been completed
  const [pendingTwinMessage, setPendingTwinMessage] = useState<TwinMessage | null>(null)

  // we store a new twin conversation in local state so that we can update the recent conversations and conversations grouped by twin once we have finished the while loop for the streaming call
  const [tempTwinConversation, setTempTwinConversation] = useState<TwinConversation | null>(null)
  useEffect(() => {
    if (!conversation && tempTwinConversation && twin) {
      hydrateRecentConversations?.(twin, tempTwinConversation)
      hydrateConversationsGroupedByTwin?.(twin, tempTwinConversation)
      setTempTwinConversation(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempTwinConversation])

  // temp local state used to update the verbose output once we're done streaming
  const [tempVerboseOutput, setTempVerboseOutput] = useState<string>('')
  useEffect(() => {
    if (tempVerboseOutput) {
      setVerboseOutput(tempVerboseOutput)
      setTempVerboseOutput('')
    }
  }, [tempVerboseOutput, setVerboseOutput])

  const [tempContextSources, setTempContextSources] = useState<TwinContextSource[]>([])
  useEffect(() => {
    if (tempContextSources.length > 0) {
      setContextSources(tempContextSources)
      setTempContextSources([])
    }
  }, [tempContextSources, setContextSources])

  const flags = useFlag('fake-response')
  const isFakeResponseActive = flags.feature.active

  const { refetchRecentConversations } = useFetchRecentConversations({
    shouldFetchOnMount: false,
  })
  const { refetchConversationsGroupedByTwin } = useFetchConversationsGroupedByTwin({
    groupByTwin: true,
    shouldFetchOnMount: false,
  })

  const onSendQuestion = async (retriedQuestion?: string) => {
    setIsSubmitting(true)

    // debug settings validation
    const isDebugSettingsValid = validateDebugSettings(temperature, maxTemperature, distance)

    if (
      !activeOrganization ||
      (!question && !retriedQuestion) ||
      !relevance ||
      (user?.is_system_admin && !isDebugSettingsValid)
    ) {
      setIsSubmitting(false)
      return null
    }

    if (!conversation) {
      setIsCreatingNewConversation?.(true)
    }

    const debugProperties = getDebugProperties(user?.is_system_admin ?? false, {
      contextSize: parseInt(contextSize),
      maxContextSize: parseInt(maxContextSize),
      relevance,
      searchType,
      contextType,
      temperature,
      maxTemperature,
      modelType,
      summaryModelType,
      emailIntentModelType,
      distance,
      combineSearch,
      mapReduceDepth: parseInt(mapReduceDepth),
    })

    // if the conversation type is chat and streaming is toggled on, we should use the streaming endpoint
    // otherwise, use the regular twinsAsk endpoint
    if (conversationType === TwinConversationType.chat && isStreamingToggled) {
      try {
        await streamedTwinsAsk(
          activeOrganization,
          twinIdToUse,
          question,
          user?.is_system_admin ?? false,
          setPendingTwinMessage,
          setTempTwinMessage,
          setStreamedMessage,
          setTempTwinConversation,
          false,
          {
            conversation,
            relevanceConfig: debugProperties,
            retriedQuestion,
            setTempContextSources,
            setTempVerboseOutput,
            setConversationId,
          },
          isMobiusToggled
        )
        setToast({ title: 'Message sent', severity: 'success' })
        setQuestion('')
      } catch (e) {
        setToast({
          title: 'Failed to send message',
          severity: 'error',
        })
      } finally {
        setIsSubmitting(false)
      }
    } else {
      try {
        const response = await twinsAsk(
          activeOrganization,
          {
            twin_id: twinIdToUse,
            conversation_id: conversation?.id,
            question: retriedQuestion || question,
            ...(user?.is_system_admin && { relevance_config: debugProperties }),
            timezone_offset: -new Date().getTimezoneOffset(),
          },
          {
            fake_response: isFakeResponseActive,
            verbose: user?.is_system_admin,
          }
        )

        // we should only refetch the sidebar if we have created a new conversation
        if (!conversation?.id) {
          // if we are creating a new conversation when not using streaming API, we should make sure the flag is set off so that we can properly fetch the
          // conversation messages
          setIsCreatingNewConversation?.(false)
          refetchRecentConversations()
          refetchConversationsGroupedByTwin()
          setConversationId?.(response.data.twin_conversation.id)
        }

        // debugging purposes
        if (response.data.verbose_output) {
          setVerboseOutput(response.data.verbose_output)
        }
        if (response.data.context_sources) {
          setContextSources(response.data.context_sources)
        }

        if (conversation?.id) {
          hydrateConversationMessages(response.data.twin_message)

          // we want the latest conversation to be the one that is selected
          setConversationId?.(conversation?.id)
        }
        setToast({ title: 'Message sent', severity: 'success' })
        setQuestion('')
      } catch (error) {
        if (error instanceof AxiosError && error.response) {
          if (error.response.data.twin_conversation && error.response.data.twin_message) {
            if (!conversation?.id) {
              setIsCreatingNewConversation?.(false)
              refetchRecentConversations()
              refetchConversationsGroupedByTwin()
              setConversationId?.(error.response.data.twin_conversation.id)
            } else {
              hydrateConversationMessages(error.response.data.twin_message)

              // we want the latest conversation to be the one that is selected
              setConversationId?.(conversation?.id)
            }
          }
        }
        setToast({ title: 'Failed to send message', severity: 'error' })
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const loadingText = () => {
    if (twin?.name) {
      return `${twin.name} is generating a response...`
    } else if (user) {
      return `${user?.name}'s twin is generating a response...`
    } else if (anonymousTwin) {
      return `${anonymousTwin?.name || anonymousTwin.user.name}'s twin is generating a response...`
    }
  }

  const onRetryClick = async (question: string) => {
    setImmediate(() => {
      setQuestion(question)
      onSendQuestion(question)
    })
  }

  if (isLoadingConversationMessages) {
    return (
      <Stack height="100vh" width="100%" textAlign="center" justifyContent="center" alignItems="center">
        <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
      </Stack>
    )
  }

  if ((isReviewMode && !conversation) || (!twinIdToUse && !conversation) || !twin) {
    return (
      <Stack height="100vh" width="100%" textAlign="center" justifyContent="center">
        <Typography
          variant="h3.75"
          sx={{
            color: COLORS.GRAY_4,
          }}>
          {emptyStateText || 'Select a conversation or start a new one'}
        </Typography>
      </Stack>
    )
  }

  const renderHeader = () => {
    // in review mode, we should display the asker user in the header rather than the twin
    if (isReviewMode && askerUser) {
      return (
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          {isMobile && (
            <IconButton
              onClick={() => {
                // clear the conversation id
                setConversationId?.('')
                navigate('/jelledbots?isReviews=true')
              }}>
              <ArrowBackIcon
                sx={{
                  fill: COLORS.GRAY_3,
                  p: 0,
                }}
              />
            </IconButton>
          )}
          <ProfilePhoto src={askerUser.picture} size="36px" name={askerUser.name || askerUser.email} />

          <Typography variant="body1W">{askerUser.name}</Typography>
        </Stack>
      )
    }
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        {isMobile && (
          <IconButton
            onClick={() => {
              // clear the conversation id
              setConversationId?.('')
              navigate('/jelledbots')
            }}>
            <ArrowBackIcon
              sx={{
                fill: COLORS.GRAY_3,
                p: 0,
              }}
            />
          </IconButton>
        )}
        <ProfilePhoto src={twin.picture} size="36px" name={twin.name || twin.user.email} />

        <Stack>
          <Typography variant="body1W">{twin.name}</Typography>
          <Typography variant="body0" color="#666666">
            {twin.user.title || ''}
          </Typography>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack
      p={2}
      width="auto"
      height="100vh"
      sx={{
        backgroundColor: COLORS.PURE_WHITE,
      }}>
      {renderHeader()}

      <Divider
        sx={{
          mt: isMobile ? 0 : 2,
          mb: 2,
          color: COLORS.GRAY_5,
          width: '100%',
          height: '1px',
        }}
      />

      {twin.public_summary && (
        <Stack
          borderRadius="10px"
          p={2}
          mb={3}
          sx={{
            backgroundColor: COLORS.GRAY_8,
          }}>
          <Typography variant="body0">{twin.public_summary}</Typography>
        </Stack>
      )}

      <MessagesScrollView
        isReviewMode={isReviewMode}
        twin={twin}
        twinOrgId={twinOrgId}
        conversationMessages={conversationMessages}
        hydrateConversationMessage={hydrateConversationMessage}
        onSendQuestion={onSendQuestion}
        onRetryClick={onRetryClick}
        hydrateConversationReviewRequests={hydrateConversationReviewRequests}
        streamedMessage={streamedMessage}
        pendingTwinMessage={pendingTwinMessage}
      />

      {isSubmitting &&
        !streamedMessage && ( // we don't want to show the loading indicator if we have started streaming messages
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
            <Typography variant="h6">{loadingText()}</Typography>
          </Stack>
        )}

      {!isReviewMode && (
        <TextField
          multiline={true}
          maxRows={3}
          placeholder="What would you like to know?"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onSendQuestion()
            }
          }}
          value={question}
          onChange={onQuestionChange}
          disabled={isSubmitting}
          fullWidth
          InputProps={{
            endAdornment: (
              <LoadingButton
                loading={isSubmitting}
                disabled={!question}
                disableRipple={true}
                onClick={() => {
                  onSendQuestion()
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}>
                <SendIcon
                  style={{
                    height: '32px',
                    width: '32px',
                    padding: '8px',
                    borderRadius: '8px',
                    background: question ? COLORS.JELLED_PRIMARY_BLUE : 'transparent',
                    fill: isSubmitting ? 'transparent' : question ? COLORS.PURE_WHITE : COLORS.GRAY_3,
                    transition: 'background 0.5s, fill 0.5s',
                  }}
                />
              </LoadingButton>
            ),
          }}
          sx={{
            marginTop: '8px',
            marginBottom: '16px',
            input: {
              fontSize: '14px',
              fontWeight: 600,
            },
            '&::placeholder': {
              fontSize: '14px',
              fontWeight: 600,
            },
          }}
        />
      )}
      {!isReviewMode && (
        <DebugSettings
          isSystemAdmin={user?.is_system_admin || false}
          twin={twin}
          modelType={modelType}
          setModelType={setModelType}
          summaryModelType={summaryModelType}
          setSummaryModelType={setSummaryModelType}
          emailIntentModelType={emailIntentModelType}
          setEmailIntentModelType={setEmailIntentModelType}
          temperature={temperature}
          setTemperature={setTemperature}
          maxTemperature={maxTemperature}
          setMaxTemperature={setMaxTemperature}
          relevance={relevance}
          setRelevance={setRelevance}
          contextType={contextType}
          setContextType={setContextType}
          contextSize={contextSize}
          setContextSize={setContextSize}
          maxContextSize={maxContextSize}
          setMaxContextSize={setMaxContextSize}
          searchType={searchType}
          setSearchType={setSearchType}
          distance={distance}
          setDistance={setDistance}
          combineSearch={combineSearch}
          setCombineSearch={setCombineSearch}
          verboseOutput={verboseOutput}
          setIsVerboseLogModalOpen={setIsVerboseLogModalOpen}
          isVerboseLogModalOpen={isVerboseLogModalOpen}
          contextSources={contextSources}
          mapReduceDepth={mapReduceDepth}
          setMapReduceDepth={setMapReduceDepth}
          conversationType={conversationType}
          setConversationType={setConversationType}
          isConversationSelectorDisabled={false}
          isDarkBackdrop={true}
          isStreamingToggled={isStreamingToggled}
          setIsStreamingToggled={setIsStreamingToggled}
          isMobiusToggled={isMobiusToggled}
          setIsMobiusToggled={setIsMobiusToggled}
        />
      )}
    </Stack>
  )
}

const Conversations = ({ updateOnboardingStep }: { updateOnboardingStep: () => void }) => {
  const isMobile = useCheckMobileScreen()
  const user = useRecoilValue(authenticatedUserAtom)

  const [searchParams] = useSearchParams()
  const conversationIdFromParams = searchParams.get('conversationId')

  // used for debugging purposes
  const [verboseOutput, setVerboseOutput] = useState<string>('')
  const [contextSources, setContextSources] = useState<TwinContextSource[]>([])

  const [conversationId, setConversationId] = useState<string>(conversationIdFromParams ?? '')
  // this flag is used to make sure that we don't prematurely fetch conversation messages if we are creating a new conversation
  const [isCreatingNewConversation, setIsCreatingNewConversation] = useState<boolean>(false)

  const [isAddConversationModalOpen, setIsAddConversationModalOpen] = useState<boolean>(false)
  const onAddConversationClick = () => {
    setIsAddConversationModalOpen(true)
  }

  const hasConversationsFtuxModalBeenShown = !!user?.onboarding_steps_completed.includes(
    OnboardingStep.conversation_modal_shown
  )
  const [isConversationsFtuxModalOpen, setIsConversationsFtuxModalOpen] = useState<boolean>(
    !hasConversationsFtuxModalBeenShown
  )

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const recentConversations = useRecoilValue(recentConversationsAtom)
  const setRecentConversations = useSetRecoilState(recentConversationsAtom)
  const setConversationsGroupedByTwin = useSetRecoilState<TwinConversationGroup[]>(conversationsGroupedByTwinAtom)
  const conversation = recentConversations
    .map((rc) => rc.twin_conversations)
    .flat()
    .find((c) => c.id === conversationId)

  // fetch conversation messages
  const {
    conversationMessages,
    setConversationMessages,
    isLoading: isLoadingConversationMessages,
  } = useFetchConversationMessages(conversation?.twin_id ?? '', conversationId, {
    isCreatingNewConversation,
  })

  // this is used when we want to update a specific twin message in recoil
  const hydrateConversationMessage = (twinMessage: TwinMessage) => {
    setConversationMessages((prev) => {
      const updatedConversationMessages = prev.map((cm) => {
        if (cm.id === twinMessage.id) {
          return twinMessage
        }
        return cm
      })
      return updatedConversationMessages
    })
  }

  const hydrateConversationMessages = (twinMessage: TwinMessage) => {
    // we need to update conversationMessages in recoil
    setConversationMessages((prev) => {
      return [...prev, twinMessage]
    })
  }

  const hydrateRecentConversations = (twin: Twin, twinConversation: TwinConversation) => {
    setRecentConversations((prev) => {
      // add the new conversation to the top of the list
      return [{ twin, twin_conversations: [twinConversation] }, ...prev]
    })
  }

  const hydrateConversationsGroupedByTwin = (twin: Twin, twinConversation: TwinConversation) => {
    setConversationsGroupedByTwin((prev) => {
      if (!prev) {
        return []
      }

      // if prev was an empty array, we should return the updatedConversationsGroupedByTwin
      if (prev.length === 0) {
        return [
          {
            twin,
            twin_conversations: [twinConversation],
          },
        ]
      }

      const updatedConversationsGroupedByTwin = prev.map((c) => {
        if (c.twin.id === twin.id) {
          return {
            ...c,
            twin_conversations: [twinConversation, ...c.twin_conversations],
          }
        }
        return c
      })

      return updatedConversationsGroupedByTwin
    })
  }

  // hydration is required to show the newly created conversation in the sidebar regardless of the view type
  const onConversationAdded = (
    twin: Twin,
    twinConversation: TwinConversation,
    verboseOutput: string,
    contextSources?: TwinContextSource[]
  ) => {
    hydrateRecentConversations(twin, twinConversation)
    hydrateConversationsGroupedByTwin(twin, twinConversation)
    // we want the latest conversation to be the one that is selected
    setConversationId(twinConversation.id)
    setVerboseOutput(verboseOutput)
    if (contextSources) {
      setContextSources(contextSources)
    }
  }

  return (
    <Stack
      sx={{
        px: isMobile ? 2 : 0,
      }}>
      <Typography variant={isMobile ? 'h4' : 'h2'} mb={2}>
        Conversations
      </Typography>

      {!isMobile && (
        <Typography variant={isMobile ? 'h4' : 'h6'} mb={2} color="#999999">
          View conversations with twins
        </Typography>
      )}

      {isMobile ? (
        <>
          <ConversationsSidebar
            onAddConversationClick={onAddConversationClick}
            conversationId={conversationId}
            setConversationId={setConversationId}
          />
          <Messages
            contextSources={contextSources}
            verboseOutput={verboseOutput}
            setContextSources={setContextSources}
            setVerboseOutput={setVerboseOutput}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
            conversation={conversation}
            setConversationId={setConversationId}
            isLoadingConversationMessages={isLoadingConversationMessages}
            hydrateConversationMessages={hydrateConversationMessages}
            hydrateConversationMessage={hydrateConversationMessage}
            conversationMessages={conversationMessages}
            setIsCreatingNewConversation={setIsCreatingNewConversation}
            hydrateRecentConversations={hydrateRecentConversations}
            hydrateConversationsGroupedByTwin={hydrateConversationsGroupedByTwin}
          />
        </>
      ) : (
        <Stack direction="row" spacing={4}>
          <ConversationsSidebar
            onAddConversationClick={onAddConversationClick}
            conversationId={conversationId}
            setConversationId={setConversationId}
          />
          <Messages
            contextSources={contextSources}
            setContextSources={setContextSources}
            verboseOutput={verboseOutput}
            setVerboseOutput={setVerboseOutput}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
            conversation={conversation}
            isLoadingConversationMessages={isLoadingConversationMessages}
            hydrateConversationMessages={hydrateConversationMessages}
            hydrateConversationMessage={hydrateConversationMessage}
            conversationMessages={conversationMessages}
            setConversationId={setConversationId}
            setIsCreatingNewConversation={setIsCreatingNewConversation}
            hydrateRecentConversations={hydrateRecentConversations}
            hydrateConversationsGroupedByTwin={hydrateConversationsGroupedByTwin}
          />
        </Stack>
      )}

      {isAddConversationModalOpen && (
        <AddConversationModal
          setConversationId={setConversationId}
          onConversationAdded={onConversationAdded}
          onClose={() => setIsAddConversationModalOpen(false)}
        />
      )}

      {isConversationsFtuxModalOpen && (
        <ConversationsFtuxModal
          onClose={() => {
            setIsConversationsFtuxModalOpen(false)
            setIsAddConversationModalOpen(true)
            updateOnboardingStep()
          }}
        />
      )}
    </Stack>
  )
}

export default withOnboardingStep(Conversations, OnboardingStep.conversation_modal_shown)
